import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  addAuditAccount,
  addAuditFrameworks,
  getAudit,
  removeAuditAccount,
  removeAuditFramework,
  rescanAudit,
  unwrapDelete,
  unwrapGet,
  unwrapPost,
  updateAuditAccount,
} from "../../../../helpers/auditHub_helper";
import { AUDIT_STATUSES, MAX_ACCOUNT_REGIONS } from "../constants";
import { AWS_REGION_CODES, frameworkLabel, scorePercent } from "../utils";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, outlinedButtonSx } from "../theme";
import AddAccountDialog from "./AddAccountDialog";
import RemoveScopeDialog from "./RemoveScopeDialog";

/** There is no scan-status route; scores move when `latestRunId` reaches the triggered run. */
const SCAN_POLL_MS = 20000;
const SCAN_POLL_LIMIT_MS = 30 * 60 * 1000;

const LABEL_COLOR = "#334155";

const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  flexShrink: 0,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#334155" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9", backgroundColor: "#f8fafc" },
};

const ACTIVE_ACTION_BTN_SX = {
  ...ACTION_BTN_SX,
  color: AH_ACCENT,
  borderColor: "#fed7aa",
  backgroundColor: "#fff4eb",
  "&:hover": { backgroundColor: "#ffedd5", color: AH_ACCENT_HOVER },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
};

const headerButtonSx = {
  ...outlinedButtonSx,
  height: 32,
  minHeight: 32,
  fontSize: "0.8125rem",
  color: AH_ACCENT,
  borderColor: AH_ACCENT,
  "&:hover": {
    color: AH_ACCENT_HOVER,
    borderColor: AH_ACCENT_HOVER,
    backgroundColor: "#fff4eb",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: AH_ACCENT,
    borderColor: AH_ACCENT,
    opacity: 0.5,
  },
};

const scopeChipSx = {
  height: 26,
  maxWidth: "100%",
  borderRadius: "6px",
  backgroundColor: "#fff4eb",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: "0.8125rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
  "& .MuiChip-deleteIcon": {
    color: "#9a3412",
    fontSize: 16,
    "&:hover": { color: "#dc2626" },
  },
};

const regionChipSx = {
  height: 22,
  maxWidth: "100%",
  borderRadius: "6px",
  backgroundColor: AH_TRACK,
  border: `1px solid ${AH_BORDER}`,
  color: AH_TEXT,
  fontSize: "0.75rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
};

const OFFERED_REGIONS = new Set(AWS_REGION_CODES);

/** `regions` is the selected region codes. An empty list is every region, matching create and add account. */
const regionCodes = (value) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((region) => (typeof region === "string" ? region.trim() : ""))
    .filter((code) => OFFERED_REGIONS.has(code));
};

const sameRegionSet = (left, right) => {
  if (left.length !== right.length) return false;
  const known = new Set(left);
  return right.every((code) => known.has(code));
};

const pickChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const statusBadgeSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 0.75,
  height: 20,
  borderRadius: "6px",
  backgroundColor: AH_TRACK,
  color: AH_MUTED,
  fontSize: "0.6875rem",
  fontWeight: 600,
  lineHeight: "20px",
};

const unsupportedBadgeSx = {
  ...statusBadgeSx,
  backgroundColor: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#b91c1c",
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const scopeAddonIds = (account) =>
  (Array.isArray(account?.scopes) ? account.scopes : [])
    .map((scope) => Number(scope.framework?.complianceAddonId))
    .filter(Number.isInteger);

const accountLabelOf = (account) => account?.accountName || account?.accountNumber || "Account";

const scopeTooltip = (scope) => {
  const percent = scope.hasScanResult ? scorePercent(scope.auditScore) : null;
  const score = percent == null ? "Scan in progress" : `Score ${Math.round(percent)}%`;
  const total = Number(scope.progress?.total);
  const decided = Number(scope.progress?.decided) || 0;
  return Number.isFinite(total) && total > 0 ? `${score} · ${decided} of ${total} manual controls decided` : score;
};

const pendingAccount = (account, body, frameworks) => ({
  pending: true,
  auditAccountId: `pending:${body.accountNumber}`,
  accountNumber: body.accountNumber,
  accountName: account?.accountName || account?.accountCode || "",
  regions: Array.isArray(body.regions) ? body.regions : [],
  scopes: (Array.isArray(frameworks) ? frameworks : []).map((framework) => ({
    auditScopeId: `pending-scope:${body.accountNumber}:${framework?.complianceAddonId}`,
    framework,
    pending: true,
  })),
  body,
});

/**
 * Messages go to `onNotice` as `{ severity, message }`, or `null` to clear; the dialog shows them in its footer.
 * On a draft, `framework` is the assessment's framework and every account is brought onto it by `commit()`;
 * `supports(accountNumber, addonId)` answers from the account's catalog, or null while unknown.
 * `onScopeChange` receives `{ dirty, checking, accountCount, unsupportedCount }`.
 */
const AuditScopeSection = forwardRef(({ auditId, auditStatus, initialAccounts, framework, supports, catalogs, disabled, onChanged, onNotice, onScopeChange }, ref) => {
  const [accounts, setAccounts] = useState(Array.isArray(initialAccounts) ? initialAccounts : []);
  const [pending, setPending] = useState([]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;
  const setNotice = useCallback((notice) => {
    if (onNoticeRef.current) onNoticeRef.current(notice);
  }, []);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [regionEdits, setRegionEdits] = useState({});
  const [openRegions, setOpenRegions] = useState(null);
  const [rescanning, setRescanning] = useState(null);
  const [scanning, setScanning] = useState({});
  const scanStartedAt = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const editable = auditStatus === AUDIT_STATUSES.DRAFT;
  const canRescan = auditStatus === AUDIT_STATUSES.ACTIVE;
  const busy = disabled || Boolean(rescanning);

  useEffect(() => {
    setAccounts(Array.isArray(initialAccounts) ? initialAccounts : []);
  }, [initialAccounts]);

  useEffect(() => {
    setPending([]);
    setRegionEdits({});
    setOpenRegions(null);
  }, [auditId]);

  /** Saved accounts whose staged regions differ from what the assessment holds. */
  const regionChanges = useMemo(
    () =>
      accounts
        .filter((account) => {
          const staged = regionEdits[account.auditAccountId];
          return staged && !sameRegionSet(staged, regionCodes(account.regions));
        })
        .map((account) => ({ account, regions: regionEdits[account.auditAccountId] })),
    [accounts, regionEdits]
  );
  const editedRegionIds = useMemo(
    () => new Set(regionChanges.map((change) => change.account.auditAccountId)),
    [regionChanges]
  );

  const savedNumbers = useMemo(() => new Set(accounts.map((account) => account.accountNumber)), [accounts]);
  const visibleAccounts = useMemo(
    () => [...accounts, ...pending.filter((account) => !savedNumbers.has(account.accountNumber))],
    [accounts, pending, savedNumbers]
  );

  const frameworkId = editable && Number.isInteger(Number(framework?.complianceAddonId)) ? Number(framework.complianceAddonId) : null;

  /**
   * How each account reaches the selected framework. An account that already carries it is kept as is;
   * one whose catalog does not offer it is dropped; every other framework is taken off the rest.
   */
  const plan = useMemo(() => {
    const byAccount = new Map();
    if (frameworkId == null) return { byAccount, dirty: pending.length > 0, checking: false, unsupportedCount: 0 };
    let checking = false;
    let unsupportedCount = 0;
    let dirty = pending.length > 0;
    visibleAccounts.forEach((account) => {
      const ids = account.pending
        ? (account.body?.complianceAddonIds || []).map(Number)
        : scopeAddonIds(account);
      const hasTarget = ids.includes(frameworkId);
      const support = hasTarget ? true : supports ? supports(account.accountNumber, frameworkId) : null;
      const status = catalogs?.[account.accountNumber]?.status;
      const entry = {
        hasTarget,
        unsupported: support === false,
        checking: support == null && (!status || status === "loading"),
        otherIds: ids.filter((id) => id !== frameworkId),
      };
      if (entry.checking) checking = true;
      if (entry.unsupported) unsupportedCount += 1;
      if (!account.pending && (entry.unsupported || !hasTarget || entry.otherIds.length)) dirty = true;
      byAccount.set(account.accountNumber, entry);
    });
    return { byAccount, dirty, checking, unsupportedCount };
  }, [frameworkId, visibleAccounts, pending.length, supports, catalogs]);
  const planRef = useRef(plan);
  planRef.current = plan;

  const accountCount = visibleAccounts.length - plan.unsupportedCount;
  const dirty = plan.dirty || regionChanges.length > 0;
  useEffect(() => {
    if (onScopeChange) {
      onScopeChange({ dirty, checking: plan.checking, accountCount, unsupportedCount: plan.unsupportedCount });
    }
  }, [dirty, plan.checking, plan.unsupportedCount, accountCount, onScopeChange]);

  const reload = useCallback(async () => {
    try {
      const detail = unwrapGet(await getAudit(auditId));
      setAccounts(Array.isArray(detail.accounts) ? detail.accounts : []);
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to refresh this assessment's scope." });
    }
  }, [auditId, setNotice]);

  useEffect(() => {
    const pending = Object.entries(scanning);
    if (!pending.length) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      if (Date.now() - scanStartedAt.current > SCAN_POLL_LIMIT_MS) {
        setScanning({});
        return;
      }
      try {
        const detail = unwrapGet(await getAudit(auditId));
        if (!active) return;
        const list = Array.isArray(detail.accounts) ? detail.accounts : [];
        setAccounts(list);
        const finished = pending
          .filter(([accountId, runId]) => {
            const account = list.find((item) => item.auditAccountId === accountId);
            const scopes = Array.isArray(account?.scopes) ? account.scopes : [];
            return !scopes.length || scopes.some((scope) => scope.latestRunId === runId);
          })
          .map(([accountId]) => accountId);
        if (finished.length) {
          setScanning((prev) => {
            const next = { ...prev };
            finished.forEach((accountId) => delete next[accountId]);
            return next;
          });
          if (onChangedRef.current) onChangedRef.current();
        }
      } catch (_error) {
        // The next tick retries; a failed poll must not end the scan indicator.
      }
    }, SCAN_POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [scanning, auditId]);

  useImperativeHandle(
    ref,
    () => ({
      commit: async () => {
        const { byAccount } = planRef.current;
        const fitOf = (account) => byAccount.get(account.accountNumber);
        const dropped = accounts.filter((account) => fitOf(account)?.unsupported);
        const kept = accounts.filter((account) => !fitOf(account)?.unsupported);
        let changed = false;
        try {
          for (const account of dropped) {
            unwrapDelete(await removeAuditAccount(auditId, account.auditAccountId));
            changed = true;
          }
          for (const { account, regions } of regionChanges) {
            if (fitOf(account)?.unsupported) continue;
            unwrapPost(await updateAuditAccount(auditId, account.auditAccountId, { regions }));
            changed = true;
            setRegionEdits((prev) => {
              const next = { ...prev };
              delete next[account.auditAccountId];
              return next;
            });
          }
          // Every other framework comes off before the selected one goes on, so the assessment never holds two.
          for (const account of kept) {
            for (const addonId of fitOf(account)?.otherIds || []) {
              unwrapDelete(await removeAuditFramework(auditId, account.auditAccountId, addonId));
              changed = true;
            }
          }
          for (const account of kept) {
            if (frameworkId == null || fitOf(account)?.hasTarget) continue;
            unwrapPost(await addAuditFrameworks(auditId, account.auditAccountId, [frameworkId]));
            changed = true;
          }
        } catch (err) {
          if (changed) await reload();
          throw err;
        }

        const queue = pendingRef.current.filter((item) => !byAccount.get(item.accountNumber)?.unsupported);
        const posted = [];
        for (const item of queue) {
          const body = frameworkId == null ? item.body : { ...item.body, complianceAddonIds: [frameworkId] };
          try {
            unwrapPost(await addAuditAccount(auditId, body));
            posted.push(item.accountNumber);
          } catch (err) {
            setPending(queue.filter((entry) => !posted.includes(entry.accountNumber)));
            if (changed || posted.length) await reload();
            throw err;
          }
        }
        setPending([]);
        if (changed || posted.length) await reload();
      },
    }),
    [auditId, accounts, regionChanges, frameworkId, reload]
  );

  const afterChange = (message) => {
    setNotice({ severity: "success", message });
    reload();
    if (onChanged) onChanged();
  };

  /** The regions shown for an account: its staged edit when there is one. */
  const regionsOf = (account) =>
    !account.pending && regionEdits[account.auditAccountId]
      ? regionEdits[account.auditAccountId]
      : regionCodes(account.regions);

  /** `code` null means all regions. Changes are staged and written by `commit()`. */
  const toggleRegion = (account, code) => {
    const current = regionsOf(account);
    let next;
    if (code == null) next = [];
    else if (current.includes(code)) next = current.filter((region) => region !== code);
    else if (current.length >= MAX_ACCOUNT_REGIONS) return;
    else next = [...current, code];
    if (sameRegionSet(next, current)) return;
    if (account.pending) {
      setPending((prev) =>
        prev.map((item) => {
          if (item.accountNumber !== account.accountNumber) return item;
          const body = { ...item.body };
          if (next.length) body.regions = next;
          else delete body.regions;
          return { ...item, regions: next, body };
        })
      );
      return;
    }
    setRegionEdits((prev) => {
      const edits = { ...prev };
      if (sameRegionSet(next, regionCodes(account.regions))) delete edits[account.auditAccountId];
      else edits[account.auditAccountId] = next;
      return edits;
    });
  };

  const rescan = async (account) => {
    if (busy) return;
    setRescanning(account?.auditAccountId || "all");
    setNotice(null);
    try {
      const result = unwrapPost(await rescanAudit(auditId, account?.auditAccountId));
      const runs = Array.isArray(result?.runs) ? result.runs : [];
      const failures = Array.isArray(result?.dispatchFailures) ? result.dispatchFailures.filter(Boolean) : [];
      if (runs.length) {
        scanStartedAt.current = Date.now();
        setScanning((prev) => {
          const next = { ...prev };
          runs.forEach((run) => {
            if (run.auditAccountId && run.runId) next[run.auditAccountId] = run.runId;
          });
          return next;
        });
      }
      setNotice(
        failures.length
          ? { severity: "warning", message: `Scan dispatch: ${failures.join(" ")}` }
          : {
              severity: "success",
              message: `Scan started for ${plural(runs.length, "account")}. Scores update as each scan completes.`,
            }
      );
      if (onChanged) onChanged();
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to start the scan." });
    } finally {
      setRescanning(null);
    }
  };

  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}>Scope</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#74788d", lineHeight: 1.45 }}>
            {editable
              ? "Account, region, and framework changes are applied when you save. Removing a saved account applies immediately."
              : "Accounts and frameworks can be changed only while the assessment is a draft."}
          </Typography>
        </Box>
        {editable || (canRescan && accounts.length) ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {editable ? (
              <Tooltip title={frameworkId == null ? "Select a framework first." : ""}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => setAddAccountOpen(true)}
                    disabled={busy || frameworkId == null}
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    sx={headerButtonSx}
                  >
                    Add Account
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canRescan && accounts.length ? (
              <Button
                variant="outlined"
                onClick={() => rescan(null)}
                disabled={busy}
                startIcon={
                  rescanning === "all" ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 18 }} />
                }
                sx={headerButtonSx}
              >
                {rescanning === "all" ? "Starting" : "Rescan All"}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {visibleAccounts.length === 0 ? (
        <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED, py: 1 }}>This assessment has no accounts.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleAccounts.map((account) => {
            const scopes = Array.isArray(account.scopes) ? account.scopes : [];
            const regions = regionsOf(account);
            const label = accountLabelOf(account);
            const isScanning = Boolean(scanning[account.auditAccountId]);
            const fit = plan.byAccount.get(account.accountNumber);
            const regionsOpen = editable && openRegions === account.auditAccountId;
            return (
              <Box
                key={account.auditAccountId || account.accountNumber}
                sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "8px", backgroundColor: AH_WHITE, px: 1.5, py: 1.25 }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: AH_TEXT, overflowWrap: "anywhere" }}>
                        {label}
                      </Typography>
                      {account.pending ? (
                        <Box component="span" sx={statusBadgeSx}>
                          Not saved
                        </Box>
                      ) : null}
                      {editedRegionIds.has(account.auditAccountId) ? (
                        <Tooltip title="Region changes are applied when you save.">
                          <Box component="span" sx={statusBadgeSx}>
                            Edited
                          </Box>
                        </Tooltip>
                      ) : null}
                      {fit?.unsupported ? (
                        <Tooltip
                          title={`This account does not support ${frameworkLabel(framework)}. It is removed from the assessment when you save.`}
                        >
                          <Box component="span" sx={unsupportedBadgeSx}>
                            Not supported
                          </Box>
                        </Tooltip>
                      ) : null}
                      {fit?.checking ? (
                        <Box component="span" role="status" sx={statusBadgeSx}>
                          <CircularProgress size={10} sx={{ color: AH_MUTED }} />
                          Checking support
                        </Box>
                      ) : null}
                      {isScanning ? (
                        <Box
                          role="status"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.75,
                            height: 20,
                            borderRadius: "6px",
                            backgroundColor: "#fff4eb",
                            color: "#9a3412",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                          }}
                        >
                          <CircularProgress size={10} sx={{ color: AH_ACCENT }} />
                          Scanning
                        </Box>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED, mt: 0.25, overflowWrap: "anywhere" }}>
                      {account.accountNumber}
                    </Typography>
                    {regionsOpen ? (
                      <Box
                        role="group"
                        aria-label={`Regions for ${label}`}
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75, maxHeight: 136, overflowY: "auto", pr: 0.5 }}
                      >
                        <Chip
                          label="All Regions"
                          clickable
                          disabled={busy}
                          aria-pressed={regions.length === 0}
                          onClick={() => toggleRegion(account, null)}
                          sx={pickChipSx(regions.length === 0)}
                        />
                        {AWS_REGION_CODES.map((code) => {
                          const selected = regions.includes(code);
                          return (
                            <Chip
                              key={code}
                              label={code}
                              clickable
                              disabled={busy || (!selected && regions.length >= MAX_ACCOUNT_REGIONS)}
                              aria-pressed={selected}
                              onClick={() => toggleRegion(account, code)}
                              sx={pickChipSx(selected)}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }} aria-label={`Regions for ${label}`}>
                        {regions.length ? (
                          regions.map((code) => <Chip key={code} size="small" label={code} sx={regionChipSx} />)
                        ) : (
                          <Chip size="small" label="All regions" sx={regionChipSx} />
                        )}
                      </Box>
                    )}
                  </Box>
                  {editable || canRescan ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      {canRescan ? (
                        <Tooltip title="Rescan Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => rescan(account)}
                              aria-label={`Rescan ${label}`}
                              sx={ACTION_BTN_SX}
                            >
                              {rescanning === account.auditAccountId ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <RefreshIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {editable ? (
                        <Tooltip title={regionsOpen ? "Hide regions" : "Edit regions"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => setOpenRegions(regionsOpen ? null : account.auditAccountId)}
                              aria-label={`Edit regions for ${label}`}
                              aria-expanded={regionsOpen}
                              sx={regionsOpen ? ACTIVE_ACTION_BTN_SX : ACTION_BTN_SX}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {editable ? (
                        <Tooltip title="Remove Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => {
                                if (account.pending) {
                                  setPending((prev) => prev.filter((item) => item.accountNumber !== account.accountNumber));
                                  setOpenRegions((prev) => (prev === account.auditAccountId ? null : prev));
                                  setNotice({ severity: "success", message: `Removed ${label}.` });
                                  return;
                                }
                                setRemoveTarget({ account });
                              }}
                              aria-label={`Remove ${label}`}
                              sx={DELETE_BTN_SX}
                            >
                              <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
                {editable ? null : scopes.length === 0 ? (
                  <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED, mt: 1 }}>No framework on this account.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {scopes.map((scope) => (
                      <Tooltip key={scope.auditScopeId} title={scopeTooltip(scope)} placement="top">
                        <Chip size="small" label={frameworkLabel(scope.framework)} sx={scopeChipSx} />
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <AddAccountDialog
        auditId={auditId}
        existingAccountNumbers={visibleAccounts.map((account) => account.accountNumber)}
        frameworks={framework ? [framework] : []}
        isLive={canRescan}
        stageOnly
        open={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
        onAdded={({ account, body, frameworks, result }) => {
          setAddAccountOpen(false);
          if (body) {
            const label = accountLabelOf(account);
            setPending((prev) =>
              prev.some((item) => item.accountNumber === body.accountNumber)
                ? prev
                : [...prev, pendingAccount(account, body, frameworks)]
            );
            setNotice({ severity: "success", message: `${label} will be added when you save.` });
            return;
          }
          const label = accountLabelOf(account);
          const runId = result?.scan?.runId;
          if (runId && result?.auditAccountId) {
            scanStartedAt.current = Date.now();
            setScanning((prev) => ({ ...prev, [result.auditAccountId]: runId }));
          }
          if (result?.scanDispatchFailed) {
            setNotice({
              severity: "warning",
              message: `Added ${label}, but its scan could not be started. Use Rescan Account to try again.`,
            });
            reload();
            if (onChanged) onChanged();
            return;
          }
          afterChange(runId ? `Added ${label}. Its scan has started.` : `Added ${label}.`);
        }}
      />
      <RemoveScopeDialog
        auditId={auditId}
        target={removeTarget}
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onRemoved={({ account, scope, result }) => {
          setRemoveTarget(null);
          const removed = Number(result?.scopesRemoved) || 0;
          const base = scope
            ? `Removed ${frameworkLabel(scope.framework)} from ${accountLabelOf(account)}.`
            : `Removed ${accountLabelOf(account)}${removed ? ` and ${plural(removed, "framework")}` : ""} from this assessment.`;
          afterChange(base);
        }}
      />
    </Box>
  );
});

export default AuditScopeSection;
